import { createContext, useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";
import type { HandOverInput, LedFilesPayload } from "./types";

/** „Podgląd jak podwykonawca": włączony u nas zawęża KAŻDY odczyt modułu do tego, co
 *  widzi firma (bramka `as=contractor` po stronie backendu). Flaga siedzi w kontekście,
 *  żeby nie przewlekać jej przez wszystkie komponenty, i wchodzi na koniec klucza
 *  zapytania — unieważnianie po prefiksie dalej łapie oba warianty. */
const LedPreviewContext = createContext(false);
export const LedPreviewProvider = LedPreviewContext.Provider;
export const useLedPreview = () => useContext(LedPreviewContext);

export const ledKeys = {
  all: ["contractor-led"] as const,
  status: (connectionId: string) => ["contractor-led", "status", connectionId] as const,
  form: (connectionId: string) => ["contractor-led", "form", connectionId] as const,
  summary: (leadershipId: string) => ["contractor-led", "summary", leadershipId] as const,
  parcels: (leadershipId: string) => ["contractor-led", "parcels", leadershipId] as const,
  journal: (leadershipId: string, days: number) =>
    ["contractor-led", "journal", leadershipId, days] as const,
  active: ["contractor-led", "active"] as const,
  mine: ["contractor-led", "mine"] as const,
  byOrder: (orderId: string) => ["contractor-led", "by-order", orderId] as const,
};

export const useLedStatus = (connectionId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ledKeys.status(connectionId ?? ""),
    queryFn: () => api.getLedStatus(connectionId!),
    enabled: enabled && !!connectionId,
    staleTime: 30_000,
  });

export const useHandOverForm = (connectionId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ledKeys.form(connectionId ?? ""),
    queryFn: () => api.getHandOverForm(connectionId!),
    enabled: enabled && !!connectionId,
  });

export const usePriceList = (contractorId: string | undefined) =>
  useQuery({
    queryKey: ["contractor-price-list", contractorId],
    queryFn: () => api.getPriceList(contractorId!),
    enabled: !!contractorId,
  });

export const useHandOver = (connectionId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: HandOverInput) => api.handOver(connectionId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ledKeys.all });
      qc.invalidateQueries({ queryKey: ["power-projects"] });
      qc.invalidateQueries({ queryKey: ["nav-badges"] });
    },
  });
};

export const useLedSummary = (leadershipId: string | undefined) => {
  const preview = useLedPreview();
  return useQuery({
    queryKey: [...ledKeys.summary(leadershipId ?? ""), preview],
    queryFn: () => api.getLedSummary(leadershipId!, preview),
    enabled: !!leadershipId,
    staleTime: 15_000,
  });
};

export const useLedParcels = (leadershipId: string | undefined) => {
  const preview = useLedPreview();
  return useQuery({
    queryKey: [...ledKeys.parcels(leadershipId ?? ""), preview],
    queryFn: () => api.getLedParcels(leadershipId!, preview),
    enabled: !!leadershipId,
  });
};

export const useLedJournal = (leadershipId: string | undefined, days: number) => {
  const preview = useLedPreview();
  return useQuery({
    queryKey: [...ledKeys.journal(leadershipId ?? "", days), preview],
    queryFn: () => api.getLedJournal(leadershipId!, days, preview),
    enabled: !!leadershipId,
    staleTime: 15_000,
  });
};

export const useRevokeLeadership = (leadershipId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => api.revokeLeadership(leadershipId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ledKeys.all });
      qc.invalidateQueries({ queryKey: ["power-projects"] });
      qc.invalidateQueries({ queryKey: ["nav-badges"] });
    },
  });
};

export const useChangeCaretaker = (leadershipId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caretaker: string) => api.changeCaretaker(leadershipId, caretaker),
    onSuccess: () => qc.invalidateQueries({ queryKey: ledKeys.all }),
  });
};

export const useActiveLeaderships = (enabled: boolean) =>
  useQuery({
    queryKey: ledKeys.active,
    queryFn: api.getActiveLeaderships,
    enabled,
  });

export const useMyLedProjects = (enabled = true) =>
  useQuery({
    queryKey: ledKeys.mine,
    queryFn: api.getMyLedProjects,
    enabled,
    staleTime: 30_000,
  });

export const useMyLedProjectByOrder = (orderId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ledKeys.byOrder(orderId ?? ""),
    queryFn: () => api.getMyLedProjectByOrder(orderId!),
    enabled: enabled && !!orderId,
    retry: false,
  });

// ── etapy 2–4 ──────────────────────────────────────────────────────────────────
export const workKeys = {
  card: (lid: string, parcelId: string) => ["contractor-led", "card", lid, parcelId] as const,
  titleOptions: (lid: string) => ["contractor-led", "title-options", lid] as const,
  titleReports: (lid: string) => ["contractor-led", "title-reports", lid] as const,
  institutions: (lid: string) => ["contractor-led", "institutions", lid] as const,
  register: (lid: string) => ["contractor-led", "register", lid] as const,
  tiles: (lid: string) => ["contractor-led", "tiles", lid] as const,
  picker: (lid: string) => ["contractor-led", "orders-picker", lid] as const,
  memos: (lid: string) => ["contractor-led", "memos", lid] as const,
  reports: (lid: string) => ["contractor-led", "reports", lid] as const,
  tutorial: (lid: string, preset: string) => ["contractor-led", "tutorial", lid, preset] as const,
  analysisContractors: ["contractor-led", "analysis", "contractors"] as const,
  analysis: (contractor: string, days: number) => ["contractor-led", "analysis", contractor, days] as const,
};

export const useParcelCard = (lid: string | undefined, parcelId: string | null | undefined) => {
  const preview = useLedPreview();
  return useQuery({
    queryKey: [...workKeys.card(lid ?? "", parcelId ?? ""), preview],
    queryFn: () => api.getParcelCard(lid!, parcelId!, preview),
    enabled: !!lid && !!parcelId,
  });
};
export const useTitleOptions = (lid: string | undefined, enabled = true) =>
  useQuery({ queryKey: workKeys.titleOptions(lid ?? ""), queryFn: () => api.getTitleOptions(lid!), enabled: enabled && !!lid, staleTime: 300_000 });
export const useTitleReports = (lid: string | undefined, enabled = true) => {
  const preview = useLedPreview();
  return useQuery({ queryKey: [...workKeys.titleReports(lid ?? ""), preview], queryFn: () => api.getTitleReports(lid!, preview), enabled: enabled && !!lid });
};
export const useInstitutions = (lid: string | undefined, enabled = true) => {
  const preview = useLedPreview();
  return useQuery({ queryKey: [...workKeys.institutions(lid ?? ""), preview], queryFn: () => api.getInstitutions(lid!, preview), enabled: enabled && !!lid });
};
export const useRegister = (lid: string | undefined, enabled = true) => {
  const preview = useLedPreview();
  return useQuery({ queryKey: [...workKeys.register(lid ?? ""), preview], queryFn: () => api.getRegister(lid!, preview), enabled: enabled && !!lid });
};
export const useTiles = (lid: string | undefined, enabled = true) => {
  const preview = useLedPreview();
  return useQuery({ queryKey: [...workKeys.tiles(lid ?? ""), preview], queryFn: () => api.getTiles(lid!, preview), enabled: enabled && !!lid });
};
export const useOrdersPicker = (lid: string | undefined, enabled: boolean) =>
  useQuery({ queryKey: workKeys.picker(lid ?? ""), queryFn: () => api.getOrdersPicker(lid!), enabled: enabled && !!lid });
export const useMemos = (lid: string | undefined, enabled = true) => {
  const preview = useLedPreview();
  return useQuery({ queryKey: [...workKeys.memos(lid ?? ""), preview], queryFn: () => api.getMemos(lid!, preview), enabled: enabled && !!lid });
};
export const useReports = (lid: string | undefined, enabled = true) => {
  const preview = useLedPreview();
  return useQuery({ queryKey: [...workKeys.reports(lid ?? ""), preview], queryFn: () => api.getReports(lid!, preview), enabled: enabled && !!lid });
};
export const useRouteTutorial = (lid: string | undefined, preset: string | null) =>
  useQuery({ queryKey: workKeys.tutorial(lid ?? "", preset ?? ""), queryFn: () => api.getRouteTutorial(lid!, preset), enabled: !!lid });
export const useLeadershipAnalysis = (lid: string | undefined, days: number, enabled = true) =>
  useQuery({
    queryKey: ["contractor-led", "analysis", "leadership", lid ?? "", days],
    queryFn: () => api.getLeadershipAnalysis(lid!, days),
    enabled: enabled && !!lid,
  });

export const useAnalysisContractors = (enabled = true) =>
  useQuery({ queryKey: workKeys.analysisContractors, queryFn: api.getAnalysisContractors, enabled });
export const useAnalysis = (contractor: string | null, days: number) =>
  useQuery({ queryKey: workKeys.analysis(contractor ?? "", days), queryFn: () => api.getAnalysis(contractor!, days), enabled: !!contractor });

// ── zakładka „Pliki" ───────────────────────────────────────────────────────────
export const filesKey = (lid: string) => ["contractor-led", "files", lid] as const;

export const useLedFiles = (lid: string | undefined, enabled = true) => {
  const preview = useLedPreview();
  return useQuery({
    queryKey: [...filesKey(lid ?? ""), preview],
    queryFn: () => api.getLedFiles(lid!, preview),
    enabled: enabled && !!lid,
    staleTime: 15_000,
  });
};

/** Mutacja zakładki „Pliki": odpowiedź (świeże drzewo) trafia wprost do cache widoku,
 *  z którego poszła; dziennik i wariant podglądu są unieważniane. */
export const useLedFilesMutation = <TArgs,>(lid: string, fn: (args: TArgs) => Promise<LedFilesPayload>) => {
  const qc = useQueryClient();
  const preview = useLedPreview();
  return useMutation<LedFilesPayload, Error, TArgs>({
    mutationFn: fn,
    onSuccess: (tree) => {
      qc.setQueryData([...filesKey(lid), preview], tree);
      qc.invalidateQueries({ queryKey: filesKey(lid), refetchType: "none" });
      qc.invalidateQueries({ queryKey: ["contractor-led", "journal", lid] });
    },
  });
};

/** Zakres unieważnienia po zmianie — zawsze nagłówek (plakietki) i dziennik, do tego
 *  tylko sekcje, których zmiana dotyczy (działki z geometrią są ciężkie — nie odświeżamy
 *  ich po każdej notatce). */
export type LedScope = "card" | "register" | "tiles" | "memos" | "reports" | "all";

export const useLedMutation = <TArgs = void, TResult = unknown>(
  leadershipId: string,
  fn: (args: TArgs) => Promise<TResult>,
  scopes: LedScope[] = [],
) => {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      const lid = leadershipId;
      if (scopes.includes("all")) {
        qc.invalidateQueries({ queryKey: ledKeys.all });
      } else {
        qc.invalidateQueries({ queryKey: ledKeys.summary(lid) });
        qc.invalidateQueries({ queryKey: ["contractor-led", "journal", lid] });
        for (const s of scopes) {
          if (s === "card") {
            qc.invalidateQueries({ queryKey: ["contractor-led", "card", lid] });
            qc.invalidateQueries({ queryKey: ledKeys.parcels(lid) });
            qc.invalidateQueries({ queryKey: workKeys.titleReports(lid) });
            qc.invalidateQueries({ queryKey: workKeys.institutions(lid) });
            // Dokumenty tytułów są też w „Plikach" — zakładka ma je pokazać bez odświeżania.
            qc.invalidateQueries({ queryKey: filesKey(lid) });
            // Decyzja o tytule zmienia licznik „tyt." na chipie portfela.
            qc.invalidateQueries({ queryKey: ledKeys.active });
          }
          if (s === "register") {
            qc.invalidateQueries({ queryKey: workKeys.register(lid) });
            qc.invalidateQueries({ queryKey: ["contractor-led", "card", lid] });
          }
          if (s === "tiles") qc.invalidateQueries({ queryKey: workKeys.tiles(lid) });
          if (s === "memos") qc.invalidateQueries({ queryKey: workKeys.memos(lid) });
          if (s === "reports") qc.invalidateQueries({ queryKey: workKeys.reports(lid) });
        }
      }
      qc.invalidateQueries({ queryKey: ["nav-badges"] });
    },
  });
};
